import { useState, useRef, useEffect } from "react";
import { usePosicionDesplegable } from "../posicionDesplegable";
import DatePicker from "../forms/DatePicker";
import { clicable } from "../ClicableTeclado.jsx";

// ── Editores de propiedades de Notion ────────────────────────────────────────
// Un editor por cada tipo de propiedad que la API de Notion permite escribir.
// Los tipos calculados (formula, rollup, unique_id, created_*) se muestran en
// solo lectura porque Notion los rechaza en un PATCH.

const NOTION_COLORS = {
  default: "var(--border)", gray: "var(--border)", brown: "#e7d3c4", orange: "#fed7aa",
  yellow: "#fef08a", green: "var(--ok-line)", blue: "#bfdbfe", purple: "#e9d5ff",
  pink: "#fbcfe8", red: "var(--red-line)",
};
export const optionColor = (c) => NOTION_COLORS[c] || NOTION_COLORS.default;

const inputS = {
  width: "100%", padding: "6px 9px", borderRadius: 7, fontSize: 12.5,
  border: "1px solid var(--border-ctl)", background: "var(--surface2)",
  color: "var(--text)", fontFamily: "var(--font)", boxSizing: "border-box",
};

const chipS = (bg) => ({
  fontSize: 11.5, fontWeight: 600, padding: "2px 8px", borderRadius: 20,
  background: bg, color: "var(--text2)", display: "inline-flex", alignItems: "center", gap: 5,
});

// ── Desplegable de opciones (select / status / multi_select / people / relation)
function OptionPicker({ options, selected, multiple, onChange, placeholder = "Sin valor", allowEmpty = true }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef(null);
  const panelRef = useRef(null);
  const { estilo: posicion } = usePosicionDesplegable(open, ref, panelRef, { altoMax: 240, igualarAncho: true });

  useEffect(() => {
    function h(e) { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setQ(""); } }
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const sel = multiple ? (selected || []) : (selected != null ? [selected] : []);
  const pick = (val) => {
    if (multiple) {
      onChange(sel.includes(val) ? sel.filter(v => v !== val) : [...sel, val]);
    } else {
      onChange(selected === val && allowEmpty ? null : val);
      setOpen(false);
    }
  };
  const shown = options.filter(o => !q || o.label.toLowerCase().includes(q.toLowerCase()));

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <div {...clicable(() => setOpen(o => !o), { expanded: open })} style={{
        ...inputS, cursor: "pointer", minHeight: 30, display: "flex",
        alignItems: "center", flexWrap: "wrap", gap: 4,
      }}>
        {sel.length === 0
          ? <span style={{ color: "var(--text3)" }}>{placeholder}</span>
          : sel.map(v => {
              const opt = options.find(o => o.value === v);
              return <span key={v} style={chipS(optionColor(opt?.color))}>{opt?.label ?? v}</span>;
            })}
      </div>
      {open && (
        <div ref={panelRef} style={{
          zIndex: 400,
          background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 9,
          boxShadow: "var(--shadow-sm)", padding: 4, ...posicion,
        }}>
          {options.length > 6 && (
            <input aria-label="Buscar opción" autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder="Buscar…"
              style={{ ...inputS, marginBottom: 4 }} />
          )}
          {allowEmpty && !multiple && (
            <div {...clicable(() => { onChange(null); setOpen(false); })} style={{ padding: "6px 9px", cursor: "pointer", fontSize: 12, color: "var(--text3)" }}>
              Vaciar
            </div>
          )}
          {shown.length === 0 && <div style={{ padding: "6px 9px", fontSize: 12, color: "var(--text3)" }}>Sin opciones</div>}
          {shown.map(o => (
            <div key={o.value} {...clicable(() => pick(o.value), { role: null })} style={{
              padding: "5px 8px", cursor: "pointer", borderRadius: 6, fontSize: 12.5,
              display: "flex", alignItems: "center", gap: 7,
              background: sel.includes(o.value) ? "var(--accent-bg)" : "transparent",
            }}>
              <input aria-label={o.label} type="checkbox" readOnly checked={sel.includes(o.value)} style={{ accentColor: "var(--accent)", width: 12, height: 12 }} />
              <span style={chipS(optionColor(o.color))}>{o.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Rango de fechas ──────────────────────────────────────────────────────────
function DateRangeEditor({ value, onChange }) {
  const start = value?.start ? value.start.slice(0, 10) : "";
  const end   = value?.end   ? value.end.slice(0, 10)   : "";
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
      {/* El DatePicker de la casa en vez del <input type="date"> del navegador,
          que pinta "dd/mm/aaaa" y un calendario distinto en cada navegador, en
          su idioma y con la semana empezando en domingo. El "hasta" sigue
          bloqueado sin un "desde" y ahora ademas no acepta una fecha anterior
          a el, que antes solo se podia meter y esperar que Notion la rechazara. */}
      <DatePicker value={start} compact nombre="Desde" placeholder="Desde"
        style={{ width: 132 }} max={end}
        onChange={v => onChange(v ? { start: v, end: end || null } : null)} />
      <span style={{ color: "var(--text3)", fontSize: 12 }}>→</span>
      <DatePicker value={end} compact nombre="Hasta" placeholder="Hasta"
        style={{ width: 132 }} disabled={!start} min={start} refDate={start}
        onChange={v => onChange({ start, end: v || null })} />
      {(start || end) && (
        <button onClick={() => onChange(null)} title="Vaciar fecha"
          style={{ border: "none", background: "transparent", color: "var(--text3)", cursor: "pointer", fontSize: 13 }}>✕</button>
      )}
    </div>
  );
}

// ── Valor de solo lectura (formula, rollup, files, created_by…) ──────────────
function ReadOnlyValue({ type, value }) {
  let text;
  if (value == null || value === "") text = "—";
  else if (Array.isArray(value))     text = value.map(v => (typeof v === "object" ? v?.name ?? v?.url ?? "" : v)).filter(Boolean).join(", ") || "—";
  else if (typeof value === "object") text = value.name ?? value.start ?? JSON.stringify(value);
  else if (typeof value === "boolean") text = value ? "Sí" : "No";
  else text = String(value);

  return (
    <div style={{ fontSize: 12.5, color: "var(--text2)", padding: "6px 0", display: "flex", alignItems: "center", gap: 6 }}>
      {text}
      <span title={`Notion no permite editar propiedades de tipo ${type}`} style={{ fontSize: 11, color: "var(--text3)" }}>🔒</span>
    </div>
  );
}

// ── Editor de una propiedad ──────────────────────────────────────────────────
export function PropertyEditor({ def, value, onChange, users = [], relationOptions = [] }) {
  if (!def.editable) return <ReadOnlyValue type={def.type} value={value} />;

  switch (def.type) {
    case "title":
    case "rich_text":
      return (
        <textarea aria-label={def.name} rows={def.type === "title" ? 1 : 2} value={value ?? ""}
          onChange={e => onChange(e.target.value)}
          style={{ ...inputS, resize: "vertical", lineHeight: 1.5 }} />
      );

    case "number":
      return <input aria-label={def.name} type="number" value={value ?? ""} onChange={e => onChange(e.target.value === "" ? null : Number(e.target.value))} style={inputS} />;

    case "url":
    case "email":
    case "phone_number":
      return (
        <input aria-label={def.name} type={def.type === "email" ? "email" : "text"} value={value ?? ""}
          placeholder={def.type === "url" ? "https://…" : ""}
          onChange={e => onChange(e.target.value || null)} style={inputS} />
      );

    case "checkbox":
      return (
        <label style={{ display: "inline-flex", alignItems: "center", gap: 7, fontSize: 12.5, cursor: "pointer", color: "var(--text)" }}>
          <input type="checkbox" checked={!!value} onChange={e => onChange(e.target.checked)}
            style={{ accentColor: "#10b981", width: 15, height: 15 }} />
          {value ? "Sí" : "No"}
        </label>
      );

    case "select":
    case "status":
      return (
        <OptionPicker
          options={(def.options || []).map(o => ({ value: o.name, label: o.name, color: o.color }))}
          selected={value} onChange={onChange} allowEmpty={def.type !== "status"} />
      );

    case "multi_select":
      return (
        <OptionPicker multiple
          options={(def.options || []).map(o => ({ value: o.name, label: o.name, color: o.color }))}
          selected={value || []} onChange={onChange} placeholder="Sin etiquetas" />
      );

    case "people":
      return (
        <OptionPicker multiple
          options={users.map(u => ({ value: u.id, label: u.name, color: "blue" }))}
          selected={(value || []).map(p => (typeof p === "string" ? p : p.id))}
          onChange={onChange} placeholder="Sin asignar" />
      );

    case "relation":
      return (
        <OptionPicker multiple
          options={relationOptions.map(o => ({ value: o.id, label: `${o.icon ? o.icon + " " : ""}${o.title}`, color: "gray" }))}
          selected={value || []} onChange={onChange} placeholder="Sin relaciones" />
      );

    case "date":
      return <DateRangeEditor value={value} onChange={onChange} />;

    default:
      return <ReadOnlyValue type={def.type} value={value} />;
  }
}

// ── Detección de propiedades "especiales" a partir del esquema ───────────────
const DONE_KEYS = ["completada", "completado", "complete", "completed", "done", "hecho", "finalizado", "terminado", "listo", "realizada", "realizado"];
const norm = (s) => (s || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");

// Propiedad que gobierna las columnas del tablero (misma logica que el backend)
export function findStatusProp(schema = []) {
  return schema.find(p => p.type === "status")
      ?? schema.find(p => p.type === "select" && !norm(p.name).includes("prior"))
      ?? null;
}

// Checkbox de finalizacion ("Completada" y similares)
export function findCompletedProp(schema = []) {
  const checkboxes = schema.filter(p => p.type === "checkbox");
  return checkboxes.find(p => DONE_KEYS.some(k => norm(p.name).includes(k))) ?? checkboxes[0] ?? null;
}

export function findTitleProp(schema = []) {
  return schema.find(p => p.type === "title") ?? null;
}

export { inputS as notionInputStyle };
